// Opening move: useAnalyzerHealth hook for gating stress features
// Big play: Checks backend analyzer status and caches result with periodic refresh
// Time to tackle the tricky bit: Provides graceful degradation when backend unavailable

import { useState, useEffect, useCallback } from 'react'
import { createDefaultStressCoordinatorService } from '../services/StressCoordinatorService'
import type { AnalyzerStatus } from '../services/StressCoordinatorService'

/**
 * Hook result containing analyzer health status and refresh function
 *
 * Main play: Exposes health data and manual refresh capability
 */
export interface AnalyzerHealthResult {
  /** Whether the analyzer backend is healthy and ready */
  healthy: boolean
  /** Whether spaCy NLP model is loaded */
  spacyLoaded: boolean
  /** Whether CMU pronunciation dictionary is loaded */
  cmuLoaded: boolean
  /** Error message if health check failed */
  error?: string
  /** Whether the initial health check is still loading */
  loading: boolean
  /** Manually trigger a health check refresh */
  refresh: () => void
}

/**
 * Configuration for analyzer health hook
 */
export interface UseAnalyzerHealthConfig {
  /** Refresh interval in milliseconds (default: 60000 = 1 minute) */
  refreshInterval?: number
  /** Whether to enable automatic refresh (default: true) */
  enableAutoRefresh?: boolean
  /** Base URL for backend API (default: empty string for relative URLs) */
  baseUrl?: string
}

/**
 * useAnalyzerHealth - React hook for monitoring stress analyzer backend health
 *
 * Main play: Checks analyzer status on mount and optionally refreshes on interval
 * Victory lap: Enables UI to disable stress features when backend is unhealthy
 *
 * Epic 05 Scope 02: Fix 1 - Analyzer health gating + UX messaging
 * Reference: .agent_process/work/lexical_epic_05_scope_02_stress_auto_load/iteration_01_a
 *
 * @param config - Configuration options for health checking
 * @returns Health status and refresh function
 *
 * @example
 * ```tsx
 * function Editor() {
 *   const { healthy, loading, error } = useAnalyzerHealth()
 *
 *   if (!healthy && !loading) {
 *     return <div>Stress tools unavailable: {error}</div>
 *   }
 *
 *   return <BaselineEditor enableStressMarking={healthy} />
 * }
 * ```
 */
export function useAnalyzerHealth(config: UseAnalyzerHealthConfig = {}): AnalyzerHealthResult {
  const {
    refreshInterval = 60000, // 1 minute default
    enableAutoRefresh = true,
    baseUrl,
  } = config

  // Opening move: Track health status state
  const [status, setStatus] = useState<AnalyzerStatus>({
    healthy: true, // Optimistic default - assume healthy until proven otherwise
    spacy_model_loaded: true,
    cmu_dict_loaded: true,
  })
  const [loading, setLoading] = useState(true)

  // Main play: Create coordinator service instance (memoized)
  const [service] = useState(() => createDefaultStressCoordinatorService({ baseUrl }))

  // Big play: Function to check analyzer status
  const checkHealth = useCallback(async () => {
    try {
      const health = await service.getAnalyzerStatus()
      setStatus(health)
      setLoading(false)

      // Debug logging only in development
      if (process.env.NODE_ENV === 'development') {
        console.log('[useAnalyzerHealth] Status check:', {
          healthy: health.healthy,
          spacyLoaded: health.spacy_model_loaded,
          cmuLoaded: health.cmu_dict_loaded,
          error: health.error,
        })
      }
    } catch (error) {
      // Time to tackle the tricky bit: Handle check failures gracefully
      const errorMsg = error instanceof Error ? error.message : 'Unknown error'
      console.error('[useAnalyzerHealth] Health check failed:', errorMsg)

      setStatus({
        healthy: false,
        spacy_model_loaded: false,
        cmu_dict_loaded: false,
        error: errorMsg,
      })
      setLoading(false)
    }
  }, [service])

  // Victory lap: Check health on mount
  useEffect(() => {
    checkHealth()
  }, [checkHealth])

  // Time to tackle the tricky bit: Set up periodic refresh if enabled
  useEffect(() => {
    if (!enableAutoRefresh || refreshInterval <= 0) return

    const intervalId = setInterval(checkHealth, refreshInterval)

    return () => clearInterval(intervalId)
  }, [checkHealth, enableAutoRefresh, refreshInterval])

  // Big play: Return health status and refresh function
  return {
    healthy: status.healthy,
    spacyLoaded: status.spacy_model_loaded,
    cmuLoaded: status.cmu_dict_loaded,
    error: status.error,
    loading,
    refresh: checkHealth,
  }
}

// Victory lap: Hook complete with health checking, caching, and auto-refresh
// Enables graceful degradation when backend analyzer is unavailable
